import React, {useEffect, useMemo, useState} from 'react'
import Dropzone from 'react-dropzone'
import { useForm } from 'react-hook-form'
import {
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  ButtonProps,
  Typography
} from '@material-ui/core'
import { styled } from '@mui/material/styles';
import Alerts from '../../components/common/Alerts'
import HookForm from '../../components/common/HookForm'
import LoadingBackdrop from '../../components/common/LoadingBackdrop'
import CheckboxGroup from '../../components/common/inputs/CheckboxGroup'
import DateTimeInput from '../../components/common/inputs/DateTimeInput'
import SelectInput from '../../components/common/inputs/SelectInput'
import TextInput from '../../components/common/inputs/TextInput'
import { useFetchEventSettings } from '../../queries/events/useFetchEventSettings'
import {EventState, setAuctionURL, setEvent, useEventState} from '../../slices/eventSlice'
import styles from './AuctionSettingsForm.module.css'
import { useEditEventSettings } from './useEditEventSettings'
import {Lock, LockOpen, Close } from '@material-ui/icons'
import dayjs from "dayjs";
import {useHistory} from "react-router-dom";
import {useDispatch} from "react-redux";
import {useParams} from "react-router";
import {useAutoEvent} from "../../queries/useAutoEvent";
import {checkBidEndDate, checkBidStartDate, checkEndDate, checkEventEndDate} from "../../util/formValidation";
import {useAuthToken} from "../../slices/authSlice";
import {useFetchOrgSettings} from "../OrgSettingsPage/useFetchOrgSettings";
import {OrgState, setOrg, useOrgState} from "../../slices/orgSlice";
import {Editor, EditorTools} from "@progress/kendo-react-editor";
import imageCompression from "browser-image-compression";
import {useQueryClient} from "react-query";
import {useAuctionInfo} from "../../queries/useFetchAuctionInfo";
import axios from "axios";
import {Divider} from "@mui/material";

type FormData = {
  rownumber: number
  id: number
  name: string
  code: string
  active: boolean
  start: string
  end: string
  bidstart: string
  bidend: string
  timezone: string
  eventspaymentprocessorkey: string
  ccrequiredid: number
  checkboxbiddingsettings: {
    allowbidanonymously: boolean
    allowguestentry: boolean
    allowpaymentoffees: boolean
  }
  checkboxothersettings: {
    approvalofbidders: boolean
    allowcustomlocations: boolean
    allowtexting: boolean
    eventshowbidderpaddlenumber: boolean,
    eventusepaddlenumber: boolean
  }
  ccfee: number
  startbiddernum: number
  receipttemplateid: number
  logourl: string
  eventprivacy: string
  logo: string
  textprefix: string
  logoremoved: boolean
}

interface Params {
  id: string
}

const AuctionSettingsForm = ({ isEdit = false }) => {
  const richTextTools = [
    [EditorTools.Bold, EditorTools.Italic, EditorTools.Underline, EditorTools.Strikethrough],
    // [EditorTools.Subscript, EditorTools.Superscript],
    EditorTools.ForeColor,
    EditorTools.BackColor,
    [EditorTools.CleanFormatting],
    [EditorTools.AlignLeft, EditorTools.AlignCenter, EditorTools.AlignRight, EditorTools.AlignJustify],
    [EditorTools.Indent, EditorTools.Outdent],
    [EditorTools.OrderedList, EditorTools.UnorderedList],
    // [EditorTools.NumberedList, EditorTools.BulletedList],
    EditorTools.FontSize,
    EditorTools.FontName,
    EditorTools.FormatBlock,
    [EditorTools.SelectAll],
    [EditorTools.Undo, EditorTools.Redo],
    [EditorTools.Link, EditorTools.Unlink, EditorTools.InsertImage, EditorTools.ViewHtml],
    [EditorTools.InsertTable, EditorTools.InsertFile],
    // [EditorTools.Pdf, EditorTools.Print],
    [EditorTools.AddRowBefore, EditorTools.AddRowAfter, EditorTools.AddColumnBefore, EditorTools.AddColumnAfter],
    [EditorTools.DeleteRow, EditorTools.DeleteColumn, EditorTools.DeleteTable],
    [EditorTools.MergeCells, EditorTools.SplitCell],
  ]
  const queryClient = useQueryClient()
  const [privacyValue, setPrivacyValue] = useState('')
  const warningMsg = "WARNING: Changing the Event Code will change your event URL."
  const { id } = useParams<Params>()
  useAutoEvent(id)
  const {data} = useAuctionInfo(id)
  const event = useEventState()
  const orgState = useOrgState()

  const formMethods = useForm<FormData>()
  const { reset, watch, setValue } = formMethods
  const watchLogo = watch('logourl')
  const watchLogoData = watch('logo')
  const watcheventspaymentprocessorkey = watch('eventspaymentprocessorkey')

  const history = useHistory()
  const [dialogObj, setDialogObj] = React.useState({ msg: "", show: false })
  const [timezones, setTimezones] = React.useState([])
  const [templates, setTemplates] = React.useState([])
  const [paymentProcessors, setPaymentProcessors] = React.useState([])
  const [ccRequired, setCCRequired] = React.useState([])
  const [processorReadOnly, setProcessorReadOnly] = React.useState(false)
  const [ccrequiredenabled, setccrequiredenabled] = React.useState(false)

  const allowTextingWatch = watch('checkboxothersettings.allowtexting')
  const approvalofbidders = watch('checkboxothersettings.approvalofbidders')
  const allowcustomlocations = watch('checkboxothersettings.allowcustomlocations')
  const allowpaymentoffeesWatch = watch('checkboxbiddingsettings.allowpaymentoffees')
  const bidEndWatch = watch('bidend')

  const [auctionSettingsData, setAuctionSettingsData] = useState({
    type: "",
    status: "",
    message: "",
    eventid: 0,
    name: "",
    code: "",
    start: "",
    bidstart: "",
    bidend: "",
    end: "",
    actual: "",
    timezone: "",
    allowbidanonymously: true,
    allowguestentry: false,
    approvalofbidders: false,
    allowcustomlocations: false,
    allowtexting: false,
    textprefix: "",
    allowpaymentoffees: false,
    billme: false,
    allowattendedbytickettype: false,
    eventspaymentprocessorisreadonly: false,
    eventshowbidderpaddlenumber: false,
    eventusepaddlenumber: false,
    ccfee: 0,
    ccrequiredenabled: false,
    ccrequiredlist: [],
    logourl: "",
    eventprivacy: "",
    storeheader: "",
    storebuttonevent: "",
    storebuttoneventshow: false,
    storebuttonauction: "",
    storebuttonauctionshow: false,
    storefooter: "",
    active: false,
    paymentprocessorlist: [],
    eventspaymentprocessorkey: "",
    ccrequiredid: 0,
    receipttemplatelist: [],
    timezonelist: [],
    startbiddernum: 1,
    receipttemplateid: 1,
  })

  useEffect(() => {
    const localId = Number(id)
    console.log(localId)
    if (localId > 0) {
      getAuctionSettingsData(Number(id))
    }
  }, [])

  // useEffect(() => {
  //   console.log(auctionSettingsData)
  // }, [auctionSettingsData])

  const getAuctionSettingsData = async (eventId) => {
    const json = JSON.stringify({ eventid: eventId })
    const { data } = await axios.post('/eventsettings', json, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    const parsedData = JSON.parse(data.jsonString)
    // console.log(parsedData)

    const timezonesData = parsedData?.timezonelist?.map(timeZone => {
      return { value: timeZone.timezone, label: timeZone.timezone }
    })
    setTimezones(timezonesData)

    const paymentProcessorsData = parsedData?.paymentprocessorlist?.map(paymentPro => {
      return { value: paymentPro.paymentprocessorkey, label: paymentPro.paymentprocessorname }
    })
    setPaymentProcessors(paymentProcessorsData)

    const ccRequiredData = parsedData?.ccrequiredlist?.map(ccReq => {
      return { value: ccReq.ccrequiredid, label: ccReq.ccrequiredname }
    })
    setCCRequired(ccRequiredData)

    const ccReqData = parsedData?.ccrequiredlist?.map(ccReq => {
      return { value: ccReq.ccrequiredid, label: ccReq.ccrequiredname }
    })
    setCCRequired(ccReqData)
    setccrequiredenabled(parsedData?.ccrequiredenabled)

    setAuctionSettingsData(parsedData)
  }

  const dispatch = useDispatch()
  // dispatch(setAuctionURL(data.auctionlink))
  const authToken = useAuthToken()
  const { data: settings } = useFetchOrgSettings({ userid: authToken?.userid })

  useEffect(() => {
    setProcessorReadOnly(false)
  }, [])

  useEffect(() => {
    if (settings) {
      const orgState: OrgState = {
        name: settings?.name, message: settings?.message, isdonorsnapcustomer: settings?.isdonorsnapcustomer, twilio: settings?.twilio
      }
      // console.log(settings)
      dispatch(setOrg(orgState))
    }
  }, [settings])

  useEffect(() => {
    // console.log(data)
    dispatch(setAuctionURL(data.auctionlink))
    // dispatch(setValidAuction(data.validauction))
    // dispatch(setValidEvent(data.validevent))
  }, [data])

  const [errorMsg, setErrorMsg] = useState({
    show: false,
    msg: ""
  })


  const [successMsg, setSuccessMsg] = useState<boolean>(false)
  const [lockCode, setLockCode] = useState<boolean>(true)
  const [dialogMsg, setDialogMsg] = React.useState("");
  const [openFailure, setFailureOpen] = React.useState(false);
  const [failureMessage, setFailureMessage] = React.useState<string>("");

  var { data: eventsettings, isFetching } = useFetchEventSettings(
    Number(event.eventId),
    isEdit
  )

  useEffect(() => {
    // console.log(auctionSettingsData)
    if (auctionSettingsData?.status === 'success') {
      const timezonesTemp = auctionSettingsData?.timezonelist?.map((timeZone: any) => {
        return { value: timeZone.timezone, label: timeZone.timezone }
      })
      setTimezones(timezonesTemp)

      const templates = auctionSettingsData?.receipttemplatelist?.map(x => {
        return { label: x.receipttemplatename, value: x.receipttemplateid }
      })
      setTemplates(templates)

      const payments = auctionSettingsData?.paymentprocessorlist?.map(x => {
        return { label: x.paymentprocessorname, value: x.paymentprocessorkey }
      })
      setPaymentProcessors(payments)

      const ccRequiredData = auctionSettingsData?.ccrequiredlist?.map(ccReq => {
        return { value: ccReq.ccrequiredid, label: ccReq.ccrequiredname }
      })
      setCCRequired(ccRequiredData)
      setccrequiredenabled(auctionSettingsData?.ccrequiredenabled)

      setProcessorReadOnly(auctionSettingsData?.eventspaymentprocessorisreadonly)
    }
  }, [auctionSettingsData])

  const editMutation = useEditEventSettings()
  const eventCodeRegex = /^[-_A-Za-z0-9]+$/


  const onEditSubmit = (formData: FormData) => {
    // console.log(formData)
    const {
      checkboxbiddingsettings,
      checkboxothersettings,
      start,
      end,
      bidstart,
      bidend,
      ...rest
    } = formData

    editMutation.mutate({
      eventid: Number(event.eventId),
      start: dayjs(start).format('YYYY-MM-DDTHH:mm:ss'),
      end: dayjs(end).format('YYYY-MM-DDTHH:mm:ss'),
      bidstart: dayjs(bidstart).format('YYYY-MM-DDTHH:mm:ss'),
      bidend: dayjs(bidend).format('YYYY-MM-DDTHH:mm:ss'),
      ...rest,
      ccfee: Number(formData.ccfee),
      eventprivacy: privacyValue,
      startbiddernum: Number(formData.startbiddernum),
      ...checkboxbiddingsettings,
      ...checkboxothersettings
    },{
      onSuccess: (data) => {
        const json = JSON.parse(data.data.jsonString)
        if (json.status === "failure") {
          setFailureMessage(json.message)
          setFailureOpen(true)
        }
        else {
          console.log(json)
          const eventState: EventState = {
            eventId: json.eventid,
            eventName: json.name,
            eventCode: json.code,
            eventStart: json.start,
            eventEnd: json.end,
            bidStart: json.bidstart,
            bidEnd: json.bidend,
            allowpaymentoffees: json.allowpaymentoffees,
            ccfee: json.ccfee
          }
          dispatch(setEvent(eventState))
          queryClient.invalidateQueries(['auctioninformation', json.eventid])
          queryClient.invalidateQueries(['eventinformation', json.eventid])
          history.push('/auction/' + json.eventid )
          // setDialogMsg("Event was updated successfully.")
        }
      }
    })
  }

  const handleClickContinue = () => {
    const isLock = (warningMsg === dialogMsg);
    setDialogMsg("")
    setLockCode(!lockCode)

    if (!isLock && !isEdit) {
      history.goBack()
    }
  };

  const handleClose = () => {
    setDialogMsg("")
  };

  const closeFailure = () => {
    setFailureOpen(false);
  };

  const showDialog = () => {
    return (
        <>
          <Dialog
              open={dialogMsg.length > 0}
              // onClose={handleClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
          >
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {dialogMsg}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              {((warningMsg === dialogMsg)) && (
                  <Button onClick={handleClose}>Cancel</Button>
              )}
              <Button onClick={handleClickContinue} autoFocus>
                Continue
              </Button>
            </DialogActions>
          </Dialog>
        </>
    )
  }

  const showFailureDialog = () => {
    return (
        <>
          <Dialog
              open={openFailure}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
          >
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                <b>Failure:</b> {failureMessage}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeFailure}>Ok</Button>
            </DialogActions>
          </Dialog>
        </>
    )
  }

const FloatingButton = styled(Button)<ButtonProps>(({ theme }) => ({
  margin:'auto',
  top: 'auto',
  //  right: '40%',
  bottom: 20,
   left: '25%',
  position: 'fixed',
  width: '50%'
}))

  const onCreateSubmit = (formData: FormData) => {

    const {
      checkboxbiddingsettings,
      start,
      end,
      bidstart,
      bidend,
      checkboxothersettings,
      receipttemplateid,
      ...rest
    } = formData

    editMutation.mutate({
      ...rest,
      start: dayjs(start).format('YYYY-MM-DDTHH:mm:ss'),
      end: dayjs(end).format('YYYY-MM-DDTHH:mm:ss'),
      bidstart: dayjs(bidstart).format('YYYY-MM-DDTHH:mm:ss'),
      bidend: dayjs(bidend).format('YYYY-MM-DDTHH:mm:ss'),
      ccfee: Number(formData.ccfee),
      startbiddernum: Number(formData.startbiddernum),
      ...checkboxbiddingsettings,
      ...checkboxothersettings,
      active: true,
      eventprivacy: privacyValue
    },{
      onSuccess: (data) => {
        const json = JSON.parse(data.data.jsonString)
        if (json.status === "failure") {
          setFailureMessage(json.message)
          setFailureOpen(true)
        }
        else {
          // setDialogMsg("Event was created successfully.")
          history.push('/auction/' + json.eventid)
        }
      }
    })
  }

  const onLogoDrop = async (files: File[]) => {
    const options = {
      maxSize: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    }
    const compressedFile = await imageCompression(files[0], options)

    const fileReader = new FileReader()
    fileReader.onload = function (event) {
      const data = event.target.result as string
      setValue('logo', data.split(';base64,')[1])
    }
    fileReader.readAsDataURL(compressedFile)
  }

  const clickLockOpen = () => {
    setDialogMsg(warningMsg);
  }

  const clickLockClose = () => {
    setDialogMsg("");
    setLockCode(!lockCode)
  }

  const clickRemoveLogo = () => {
    setValue("logourl", "")
    setValue('logoremoved', true)
  }

  useEffect(() => {
    const getDefaults = async () => {
      const { data } = await axios.get('/eventsadd')
      const parsedData = JSON.parse(data.jsonString)
      console.log(parsedData)
      if (parsedData?.timezonelist) {
        const timezonesTemp = parsedData?.timezonelist?.map((timeZone: any) => {
          return { value: timeZone.timezone, label: timeZone.timezone }
        })
        setTimezones(timezonesTemp)
      }

      if (parsedData?.receipttemplatelist) {
        const templates = parsedData?.receipttemplatelist?.map(x => {
          return { label: x.receipttemplatename, value: x.receipttemplateid }
        })
        setTemplates(templates)
      }

      if (parsedData?.paymentprocessorlist) {
        const payments = parsedData?.paymentprocessorlist?.map(x => {
          return { label: x.paymentprocessorname, value: x.paymentprocessorkey }
        })
        setPaymentProcessors(payments)
      }

      if (parsedData?.ccrequiredlist) {
        const ccRequiredData = auctionSettingsData?.ccrequiredlist?.map(ccReq => {
          return {value: ccReq.ccrequiredid, label: ccReq.ccrequiredname}
        })
        setCCRequired(ccRequiredData)
        setccrequiredenabled(auctionSettingsData?.ccrequiredenabled)
      }


      if (parsedData?.eventprivacy) {
        setPrivacyValue(parsedData.eventprivacy)
      }
      setAuctionSettingsData(parsedData)
      return parsedData
    }

    if (!isEdit) {
      getDefaults()
    }
  }, [])

  useEffect(() => {
    // if (auctionSettingsData && isEdit) {
      const startDate = auctionSettingsData.start ? auctionSettingsData?.start?.split(' ').join('T') : new Date().toISOString()
      const endDate = auctionSettingsData.end ? auctionSettingsData?.end?.split(' ').join('T') : new Date().toISOString()
      const bidStartDate = auctionSettingsData.bidstart ? auctionSettingsData?.bidstart?.split(' ').join('T') : new Date().toISOString()
      const bidEndDate = auctionSettingsData.bidend ? auctionSettingsData?.bidend?.split(' ').join('T') : new Date().toISOString()
      const timeZoneForm = auctionSettingsData.timezone ? auctionSettingsData.timezone : 'Central Standard Time'
      const eventspaymentprocessorkeyForm = auctionSettingsData.eventspaymentprocessorkey ? auctionSettingsData.eventspaymentprocessorkey : 'GEM0003'


      const twilio = orgState?.twilio

      var allowtexting = auctionSettingsData.allowtexting
      if (twilio.valid === false) {
        allowtexting = false
      }

     // dispatch(setAuctionURL(data.auctionlink))

      reset({
        // rownumber: auctionSettingsData.rownumber,
        id: auctionSettingsData.eventid,
        name: auctionSettingsData.name,
        code: auctionSettingsData.code,
        active: auctionSettingsData.active,
        start: startDate,
        end: endDate,
        bidstart: bidStartDate,
        bidend: bidEndDate,
        timezone: timeZoneForm,
        eventspaymentprocessorkey: eventspaymentprocessorkeyForm,
        ccrequiredid: auctionSettingsData.ccrequiredid,
        checkboxbiddingsettings: {
          allowbidanonymously: auctionSettingsData.allowbidanonymously,
          allowguestentry: auctionSettingsData.allowguestentry,
          allowpaymentoffees: auctionSettingsData.allowpaymentoffees,
        },
        checkboxothersettings: {
          allowcustomlocations: auctionSettingsData.allowcustomlocations,
          approvalofbidders: auctionSettingsData.approvalofbidders,
          allowtexting: allowtexting,
          eventshowbidderpaddlenumber: auctionSettingsData.eventshowbidderpaddlenumber,
          eventusepaddlenumber: auctionSettingsData.eventusepaddlenumber
        },
        ccfee: auctionSettingsData.ccfee,
        startbiddernum: auctionSettingsData.startbiddernum,
        receipttemplateid: auctionSettingsData.receipttemplateid,
        textprefix: auctionSettingsData.textprefix,
        eventprivacy: auctionSettingsData.eventprivacy,
        logourl: auctionSettingsData.logourl,
        logoremoved: false
      })
      setPrivacyValue(auctionSettingsData.eventprivacy)
  }, [auctionSettingsData])

  const handleChange = (e, field) => {
      if (field.name === "checkboxothersettings.approvalofbidders" && field.value === false) {
        setDialogObj({msg: "Caution: Checking this box means that you will need to regularly check the Participants page to manually approve any newly registered participants. Participants that are not approved will not be able to bid on any items. To automatically approve all newly registered participants, leave this setting unchecked.", show: true})
      }
      else if (field.name === "checkboxothersettings.allowcustomlocations" && field.value === false) {
        setDialogObj({msg: "Caution: Checking this box means that all bidding open/close times are controlled by the Location settings. Changing the bidding open/close times on this page will not affect any existing location close times. Only use this setting if you plan to close tables at different times.", show: true})
      }
  }

  const handleCloseDate = () => {
    if (auctionSettingsData?.bidend) {
      if (new Date(auctionSettingsData?.bidend) <= new Date()) {
        if (dialogObj.show === false) {
          setDialogObj({msg: "Warning: This auction has already closed. Reopening bidding may cause unintended consequences and confusion for participants, especially in checkout. Winning bid and closing soon notifications will not be sent again for this auction. If this was a test, please create a new auction or clone this auction to set up your live event.", show: true})
        }
      }
    }
  }

  const disableTexting = () => {
    const twilio = orgState?.twilio

    return twilio.valid === false
  }

  return (
    <div className={styles.root}>
      {showDialog()}
      {showFailureDialog()}

      <>
        <Dialog
            open={dialogObj.show}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {dialogObj.msg}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDialogObj({msg: '', show: false})}}>Ok</Button>
          </DialogActions>
        </Dialog>
      </>

      <h1>Auction Settings</h1>
      <HookForm
        methods={formMethods}
        onSubmit={isEdit ? onEditSubmit : onCreateSubmit}
        className={styles.form}
        formProps={{ id: 'auctionSettingsData-form' }}>
        <LoadingBackdrop open={isFetching} />
          <TextInput name="name" label="Event/Auction Name" required tooltipText={'The name that will be displayed to your guests'}/>
        <Grid container spacing={1}>
          <Grid item xs={11} className={styles.selectcontrol}>
            <TextInput
                name="code"
                label="Event/Auction Code"
                required
                disabled={isEdit && lockCode}
                rules={{
                  pattern: {
                    value: eventCodeRegex,
                    message: 'Event/Auction code can only contain letters, numbers, hyphens, underscores, and no spaces.'
                  }
                }}
                tooltipText={'The part of the URL to specify your event. No spaces or special characters allowed. Keep it short and easy to remember.'}
            />
          </Grid>
          {isEdit && (
              <Grid item xs={1} className={styles.selectcontrol}>
                {!lockCode && <Lock fontSize="large" onClick={clickLockClose} />}
                {lockCode && <LockOpen fontSize="large" onClick={clickLockOpen} />}
              </Grid>
          )}
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={3} className={styles.selectcontrol}>
            <DateTimeInput
                name="start"
                label="Auction Open"
                defaultValue={null}
                required={true}
                tooltipText={'The time when a user can log in to your event and see the auction items'}
            />
          </Grid>

          <Grid item xs={3} className={styles.selectcontrol}>
{!allowcustomlocations &&  <DateTimeInput
                name="bidstart"
                label="Bidding Open"
                rules={{
                  validate: (val) => checkBidStartDate(val, watch),
                }}
                defaultValue={new Date().toISOString()}
                required={!allowcustomlocations}
                disabled={allowcustomlocations}
                tooltipText={'The time when a user can begin bidding on auction items'}
            />}
           {allowcustomlocations && 
              <TextInput
                name="bidstartBlank"
                label="Bidding Open"
                defaultValue='Custom by Location'
                disabled={true}
                tooltipText={ 'To enable the auction-wide bidding open/close times, disable the "Allow Custom Bidding Open/Close by Location setting below”'}
            />}
          </Grid>


          <Grid item xs={3} className={styles.selectcontrol}>
          {!allowcustomlocations && <DateTimeInput
                name="bidend"
                label="Bidding Close"
                rules={{
                  validate: (val) => checkBidEndDate(val, watch),
                }}
                defaultValue={new Date().toISOString()}
                onClose={handleCloseDate}
                required={!allowcustomlocations}
                disabled={allowcustomlocations}
                //tooltipText={"The time when no more bids will be accepted on auction items"}
            />}
             {allowcustomlocations && 
              <TextInput
                name="bidendBlank"
                label="Bidding Close"
                defaultValue='Custom by Location'
                disabled={true}
                tooltipText={ 'To enable the auction-wide bidding open/close times, disable the "Allow Custom Bidding Open/Close by Location setting below”'}
            />}
          </Grid>

          <Grid item xs={3} className={styles.selectcontrol}>
            <DateTimeInput
                name="end"
                label="Auction Close"
                rules={{
                  validate: (val) => checkEventEndDate(val, watch),
                }}
                defaultValue={new Date().toISOString()}
                required={true}
                tooltipText={"The time when the auction website and checkout pages will no longer be accessible. This is usually multiple days after bidding closes to allow collecting payment at a later date."}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={4} className={styles.selectcontrol}>
              <SelectInput
                name="timezone"
                label={'Event/Auction Timezones'}
                options={timezones}
                defaultValue={(timezones && timezones.length > 0 ? timezones[0].label : "")}
                includeNone={false}
                tooltipText={"The timezone where your event will be held. Dates/times will automatically adjust for each user’s location on their device."}
              />
          </Grid>
          <Grid item xs={4} className={styles.selectcontrol}>
              <SelectInput
                  name="eventspaymentprocessorkey"
                  label={'Payment Processor'}
                  options={paymentProcessors}
                  disabled={processorReadOnly && isEdit}
                  defaultValue={(paymentProcessors && paymentProcessors.length > 0 ? paymentProcessors[0].label : "")}
                  includeNone={false}
                  tooltipText={""}
              />
        </Grid>
          <Grid item xs={4} className={styles.selectcontrol}>
            <SelectInput
                name="ccrequiredid"
                label={'Require Credit/Debit Card on File (Stripe Only)'}
                options={ccRequired}
                disabled={!ccrequiredenabled || !watcheventspaymentprocessorkey.startsWith('STRIPE')}
                includeNone={false}
                tooltipText={""}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={4} className={styles.selectcontrol}>
            <CheckboxGroup
              name="checkboxbiddingsettings"
              onChange={(e, field) => {handleChange(e, field)}}
              options={[
                {
                  value: 'allowbidanonymously',
                  label: 'Allow Bid Anonymously',
                  tooltipText: "Give users the option to hide their name in the Bid History section."
                },
                {
                  value: 'allowguestentry',
                  label: 'Allow Guest Entry (View Only)',
                  tooltipText: "Allow users to browse your auction items without logging in. All users must log in to place a bid."
                },
                {
                  value: 'allowpaymentoffees',
                  label: 'Allow Payment of CC Fees',
                  tooltipText: "Display a checkbox during checkout for the user to add a set percentage to their payment to offset payment processing fees."
                },
              ]}
              label=""
            />
          </Grid>
          <Grid item xs={4} className={styles.selectcontrol}>
            <CheckboxGroup
              name="checkboxothersettings"
              onChange={(e, field) => {handleChange(e, field)}}
              options={[
                {
                  value: 'approvalofbidders',
                  label: 'Require Approval of Bidders',
                  tooltipText: "Do not allow bidding by users that have not been manually approved in the Participants page."
                },
                {
                  value: 'allowcustomlocations',
                  label: 'Allow custom bidding open/close by location',
                  tooltipText: "Each location will have independent open and close times for bidding. Set up these times on the Locations page."
                },
                { value: 'eventusepaddlenumber', label: 'Use manually assigned paddle number instead of auto-assigned bidder number', tooltipText: "" },

              ]}
              label=""
            />
          </Grid>
          <Grid item xs={4} className={styles.selectcontrol}>
            <CheckboxGroup
                name="checkboxothersettings"
                options={[
                  {
                    value: 'eventshowbidderpaddlenumber',
                    label: 'Show Bidder/Paddle Number to Participant',
                    tooltipText: "",
                  },
                  {
                    value: 'allowtexting',
                    label: 'Allow Texting',
                    tooltipText: "Send text message notifications to the users that have enabled them.",
                    disabled: (orgState?.twilio?.valid == false),
                    textoptions: {
                      name: "textprefix",
                      label: "Text Prefix",
                      defaultValue: "",
                      disabled: (!allowTextingWatch || orgState?.twilio?.valid == false),
                      maxLength: 50,
                      tooltipText: 'Add an optional prefix to all text message notifications to help identify your organization. Keep this prefix as short as possible. Up to 50 characters are allowed. A colon and space are added automatically.\n\nFor example: "Malaika Auction"\n"Malaika Auction: You have been outbid on the Franklin X-40 Pickleballs (100 Pack). The current bid is now $50."'
                    }
                  },
                ]}
                label=""
            />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
            <Grid item xs={6} className={styles.selectcontrol}>
              <TextInput disabled={!allowpaymentoffeesWatch} name="ccfee" label="Event/Auction CC Fee % to Add" defaultValue="2.5" endAdornment="%" required={allowpaymentoffeesWatch} tooltipText={"If the user opts to cover the payment processing fees, this percentage will be added to their payment total."} />
            </Grid>
          <Grid item xs={6} className={styles.selectcontrol}>
            <TextInput
              name="startbiddernum"
              label="Starting Bidder Number"
              defaultValue="1"
              required
              tooltipText={"The first bidder number that will be assigned to a participant followed by sequential order."}
            />
          </Grid>
        </Grid>

        <SelectInput
          name={'receipttemplateid'}
          label={'Receipt Template'}
          defaultValue={(templates && templates.length > 0 ? templates[0].label : "")}
          options={templates}
          tooltipText={"Select the PDF template that should be used for all printed/PDF generated receipts."}
        />

        <Divider textAlign={"center"}>
          <Typography variant="body2">
            Auction Image
          </Typography>
        </Divider>
        <Grid item xs={12} className={styles.selectcontrol}>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
            {watchLogo && (
                <div>
                  <div> <Close fontSize="small" onClick={clickRemoveLogo} /></div>
                  <div className={styles.logoWrapper}>
                    <img src={watchLogo} alt="logo" />
                  </div>
                </div>
            )}
            {watchLogoData && (
                <div>
                  <div style={{ paddingBottom: '2px' }}>New Auction Image</div>
                  <div className={styles.logoWrapper}>
                    <img src={`data:image/png;base64,${watchLogoData}`} alt="logo" />
                  </div>
                </div>
            )}

            <Dropzone onDrop={onLogoDrop} accept={[
              'image/jpeg', 'image/jpg', 'image/*', 'image/x-png', 'image/bmp', 'image/gif', 'image/x-ms-bmp', 'image/x-bmp']}>
              {({ getRootProps,
                  getInputProps,
                  isDragActive,
                  isDragAccept,
                  isDragReject,
                  acceptedFiles,
                  fileRejections
                }) => (
                  <section>
                    <div {...getRootProps()} className={isDragActive ? (isDragAccept ? styles.dropzoneselect : styles.dropzoneinvalid) : styles.dropzone}>
                      <input {...getInputProps()} />
                      {isDragAccept && (<h4>File is a valid image type. Go ahead and drop.</h4>)}
                      {!isDragActive && (<h4>Drag 'n' drop files here, or click to select a file</h4>)}
                      {isDragReject && (<h4>File cannot be dropped. It's an invalid type.</h4>)}
                      <h5>Item Images will be displayed with a max height of 450px. Only .jpg, .jpeg, .png, .gif, and .bmp images under 5MB are allowed.</h5>
                    </div>
                  </section>
              )}
            </Dropzone>
          </div>
        </Grid>

        <div className={styles.inputsContainer}>
          <h3>Event/Auction Privacy/Refund Policy</h3>
          <Editor
              tools={richTextTools}
              contentStyle={{ height: 230 }}
              value={privacyValue}
              onChange={(eventState) => {
                setPrivacyValue(eventState.html)
              }}
          />
        </div>

        <FloatingButton type="submit" variant="contained" className={styles.button}>
          Save
        </FloatingButton>

        <Alerts
          errorMsg={errorMsg.show ? errorMsg.msg : null}
          successMsg={successMsg ? 'Submitted successfully' : null}
        />
      </HookForm>
    </div>
  )
}

export default AuctionSettingsForm
